import { type CarerRole, type Home, UserRole } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import type { MenuItemType } from 'antd/lib/menu/hooks/useItems'

export const getNavigationTabs = (
  features: Home,
  role: CarerRole | string,
  hasRota?: boolean,
  flagsRosteringToleranceSettings?: boolean,
  flagsTravelTimeAndMileageEnabled?: boolean,
  flagsGeofencingEnabled?: boolean,
  flagsModsNeedsSettings?: boolean,
  flagsPayRatesEnabled?: boolean,
  flagsInvoicingEnabled?: boolean
) => {
  const familyEnabled = features?.family

  const consentToCareEnabled = features?.features?.consentToCare?.enabled

  const skillsAndNeedsEnabled = features?.features?.skillsAndNeeds?.enabled

  const proEnabled = features?.pro

  const isGroupUser = ['group-admin', 'group-manager'].includes(role)

  const isCareHomeAdmin = role === UserRole.careHomeAdmin

  const needsAndSkillsNavItem = flagsModsNeedsSettings
    ? {
        label: 'Needs and Skills',
        url: 'skills-and-needs',
        icon: <Icon customIcon={IconType.SettingsNeeds} />,
        subItems: [
          { label: 'Skill templates', url: 'skills-and-needs/skill-templates' },
          { label: 'Need templates', url: 'skills-and-needs/need-templates' }
        ]
      }
    : {
        label: 'Needs and Skills',
        url: 'skills-and-needs',
        icon: <Icon customIcon={IconType.SettingsNeeds} />
      }

  if (isGroupUser) {
    if (skillsAndNeedsEnabled) {
      return [needsAndSkillsNavItem]
    }
    return []
  }

  const NAV_ITEMS = [
    {
      url: 'billing',
      label: 'Billing & Subscriptions',
      icon: (
        <Icon customIcon={IconType.SettingsBilling} style={{ fill: 'none' }} />
      )
    },

    {
      label: 'Preferences',
      icon: (
        <Icon
          customIcon={IconType.SettingsPreferences}
          style={{ fill: 'red' }}
        />
      ),
      subItems: [
        { label: 'General', url: 'home-settings' },
        { label: 'Incident Reporting', url: 'incident-reports-settings' }
      ]
    },

    ...(proEnabled && isCareHomeAdmin
      ? [
          {
            url: 'rostering',
            label: 'Rostering',
            icon: <Icon customIcon={IconType.Rostering} />,
            subItems: [
              {
                id: 'contracts-and-absences-menu-item',
                label: hasRota ? 'Contract and Absence' : 'Contract',
                url: 'contracts-and-absences'
              },
              {
                id: 'care-package-menu-item',
                label: 'Care Package',
                url: 'care-package'
              },
              ...(hasRota
                ? [
                    {
                      id: 'rota-patterns-menu-item',
                      label: 'Rota Patterns',
                      url: 'rota-patterns'
                    }
                  ]
                : []),
              ...(flagsRosteringToleranceSettings
                ? [
                    {
                      id: 'warnings-menu-item',
                      label: 'Warnings',
                      url: 'warnings'
                    }
                  ]
                : []),
              ...(flagsTravelTimeAndMileageEnabled
                ? [
                    {
                      id: 'travel-time-and-mileage-menu-item',
                      label: 'Travel Time and Mileage',
                      url: 'travel-time-and-mileage'
                    }
                  ]
                : []),
              ...(flagsGeofencingEnabled
                ? [
                    {
                      id: 'geofencing-menu-item',
                      label: 'Geofencing',
                      url: 'geofencing'
                    }
                  ]
                : []),
              ...(flagsPayRatesEnabled || flagsInvoicingEnabled
                ? [
                    {
                      id: 'finance-menu-item',
                      label: flagsInvoicingEnabled ? 'Finance' : 'Pay Rates',
                      url: flagsInvoicingEnabled ? 'finance' : 'pay-rates'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(proEnabled
      ? [
          {
            url: 'monitoring-report-settings',
            label: 'Monitoring Reports',
            icon: <Icon customIcon={IconType.ReportsPreferences} />
          }
        ]
      : []),

    ...(familyEnabled
      ? [
          {
            url: 'family-settings',
            label: 'Family App',
            icon: <Icon customIcon={IconType.SettingsFamily} />
          }
        ]
      : []),

    ...(proEnabled
      ? [
          {
            url: 'zones',
            label: 'Zones',
            icon: <Icon customIcon={IconType.SettingsZones} />
          }
        ]
      : []),
    {
      url: 'perks',
      label: 'Perks',
      icon: <Icon customIcon={IconType.SettingsPerks} />
    },
    ...(consentToCareEnabled
      ? [
          {
            url: 'consent-templates',
            label: 'Consent Forms',
            icon: <Icon customIcon={IconType.SettingsConsent} />
          }
        ]
      : []),
    ...(skillsAndNeedsEnabled ? [needsAndSkillsNavItem] : []),
    ...(isCareHomeAdmin
      ? [
          {
            label: 'Privacy Officers',
            url: 'privacy-officers',
            icon: <Icon customIcon={IconType.PrivacyOfficerIcon} />
          }
        ]
      : [])
  ]

  return NAV_ITEMS as unknown as MenuItemType[]
}
